import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { useDeadlines, type Deadline } from "../api/queries.js";

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(new Date(y!, m! - 1, d!));
}

const KIND_ICON: Record<Deadline["kind"], string> = {
  // shield (URSSAF) · triangle (TVA) · building (CFE) · euro doc (impôt)
  urssaf: "M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z",
  tva: "M12 3l9 16H3zM12 10v4M12 17h.01",
  cfe: "M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 21V9h4a1 1 0 0 1 1 1v11M8 8h3M8 12h3M8 16h3",
  impot: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h4a1.5 1.5 0 0 1 0 3H9m0-3v5",
};
const KIND_COLOR: Record<Deadline["kind"], string> = {
  urssaf: "var(--primary-deep, var(--primary))",
  tva: "var(--warning, #E8A33D)",
  cfe: "var(--muted)",
  impot: "var(--success, #3F9C6D)",
};

/**
 * The Home "À venir" card: the person's next fiscal & social obligations, dates
 * derived from their déclaration rules (Settings) and amounts hinted from the
 * ledger. Person-level — never scoped by the client filter, like the ceiling.
 */
export function UpcomingCard() {
  const { t } = useTranslation();
  const { data } = useDeadlines();
  const deadlines = data?.deadlines ?? [];
  if (deadlines.length === 0) return null;

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.05)",
        padding: "14px 15px",
      }}
    >
      <div style={{ font: "600 10px Sora", letterSpacing: ".07em", textTransform: "uppercase", color: "var(--muted2)", marginBottom: 11 }}>
        {t("upcoming.title")}
      </div>
      {deadlines.map((d, i) => {
        const noteText = d.note === "acompte" ? t("upcoming.acompte") : d.note === "indicative" ? t("upcoming.indicative") : "";
        return (
          <div key={`${d.kind}-${d.dueDate}`} style={{ display: "flex", alignItems: "center", gap: 11, paddingTop: i === 0 ? 0 : 11, marginTop: i === 0 ? 0 : 11, borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
            <span style={{ width: 32, height: 32, flex: "none", borderRadius: 10, background: `color-mix(in srgb, ${KIND_COLOR[d.kind]} 16%, transparent)`, color: KIND_COLOR[d.kind], display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={KIND_ICON[d.kind]} />
              </svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: "600 13px Sora", color: "var(--text)" }}>{t(`upcoming.kind.${d.kind}`)}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>
                {d.periodLabel}
                {noteText && ` · ${noteText}`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 13.5, color: d.amountHintCents != null ? "var(--text)" : "var(--muted2)" }}>
                {d.amountHintCents != null ? formatEuros(d.amountHintCents) : "—"}
              </div>
              <div style={{ fontSize: 10, color: "var(--muted2)", marginTop: 1 }}>{fmtDate(d.dueDate)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
